import { useSearchParams } from 'react-router'

import { TablePagination } from '@/components/ui/pagination'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { SkeletonRows } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { StaffActivityKind, type StaffUserDetail, useStaffUserActivity } from '@/models/staffUser'
import { CountLabel } from './CountLabel'
import { StaffActivityItem } from './StaffActivityItem'
import { usePageParam } from './usePageParam'

const KIND_LABELS: Record<StaffActivityKind, string> = {
  [StaffActivityKind.ALL]: 'All',
  [StaffActivityKind.CALLS]: 'Calls',
  [StaffActivityKind.EMAILS]: 'Emails',
  [StaffActivityKind.SMS]: 'SMS',
  [StaffActivityKind.NOTES]: 'Notes',
  [StaffActivityKind.STATUS]: 'Status changes',
}

function isKind(value: string): value is StaffActivityKind {
  return (Object.values(StaffActivityKind) as string[]).includes(value)
}

export function StaffActivityTab({ staffUser }: { staffUser: StaffUserDetail }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = usePageParam()
  const rawKind = searchParams.get('kind') ?? ''
  const kind = isKind(rawKind) ? rawKind : StaffActivityKind.ALL
  const { activities, count, hasNextPage, hasPreviousPage, isPending, isError, isFetching } =
    useStaffUserActivity(staffUser.id, kind, page)
  // `inert`, not pointer-events: rows held over from the last filter must be unreachable by keyboard too.
  const stale = isFetching && !isPending

  function setKind(value: string) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (value === StaffActivityKind.ALL) params.delete('kind')
        else params.set('kind', value)
        params.delete('page')
        return params
      },
      { preventScrollReset: true }
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <SegmentedControl
        className="max-w-full self-start"
        value={kind}
        onValueChange={setKind}
        options={Object.values(StaffActivityKind).map((value) => ({
          value,
          label: <CountLabel label={KIND_LABELS[value]} count={staffUser.activityCounts[value]} />,
        }))}
      />
      {isPending && <SkeletonRows rows={4} rowClassName="h-24" />}
      {isError && (
        <p className="text-destructive py-6 text-center text-sm">
          Couldn’t load activity. Refresh to try again.
        </p>
      )}
      {!isPending && !isError && activities.length === 0 && (
        <p className="text-muted-foreground py-6 text-center text-sm">No activity yet.</p>
      )}
      {activities.length > 0 && (
        <div inert={stale} className={cn('flex flex-col gap-4 transition-opacity', stale && 'opacity-60')}>
          {activities.map((activity) => (
            <StaffActivityItem key={activity.id} activity={activity} />
          ))}
        </div>
      )}
      <TablePagination
        page={page}
        count={count}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={setPage}
        itemLabel="activity"
      />
    </div>
  )
}
